import { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { useLocale } from '@/i18n'

interface TocItem {
  id: string
  text: string
  level: number
}

export function TableOfContents() {
  const [headings, setHeadings] = useState<TocItem[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const [scrollProgress, setScrollProgress] = useState(0)
  const [mounted, setMounted] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0, opacity: 0 })
  const location = useLocation()
  const { t } = useLocale()
  const tocRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Track scroll progress
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
    setScrollProgress(Math.min(Math.max(progress, 0), 100))
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Parse headings from content
  useEffect(() => {
    setMounted(false)
    setHeadings([])
    setIndicatorStyle({ top: 0, height: 0, opacity: 0 })

    const timer = setTimeout(() => {
      const content = document.querySelector('.doc-content')
      if (!content) return

      const elements = content.querySelectorAll('h2, h3')
      const items: TocItem[] = []

      elements.forEach((el) => {
        const id = el.id || el.textContent?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '') || ''
        if (!el.id) {
          el.id = id
        }
        items.push({
          id,
          text: el.textContent || '',
          level: el.tagName === 'H2' ? 2 : 3,
        })
      })

      setHeadings(items)
      if (items.length > 0) {
        setActiveId(items[0].id)
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMounted(true)
        })
      })
    }, 150)

    return () => clearTimeout(timer)
  }, [location.pathname])

  // Intersection observer for active heading
  useEffect(() => {
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter(entry => entry.isIntersecting)
        if (visibleEntries.length > 0) {
          const sorted = visibleEntries.sort((a, b) => {
            const rectA = a.boundingClientRect
            const rectB = b.boundingClientRect
            return rectA.top - rectB.top
          })
          setActiveId(sorted[0].target.id)
        }
      },
      {
        rootMargin: '-80px 0px -70% 0px',
        threshold: [0, 0.5, 1]
      }
    )

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [headings])

  // Update indicator position
  useEffect(() => {
    if (!listRef.current || !activeId || !mounted) return

    const updateIndicator = () => {
      const activeElement = listRef.current?.querySelector(`[data-id="${activeId}"]`) as HTMLElement
      if (activeElement) {
        const listRect = listRef.current!.getBoundingClientRect()
        const linkElement = activeElement.querySelector('.toc-link') as HTMLElement
        if (linkElement) {
          const linkRect = linkElement.getBoundingClientRect()
          setIndicatorStyle({
            top: linkRect.top - listRect.top,
            height: linkRect.height,
            opacity: 1
          })
        }
      }
    }

    requestAnimationFrame(updateIndicator)
  }, [activeId, mounted, headings])

  // Scroll active item into view in TOC
  useEffect(() => {
    if (!contentRef.current || !activeId) return

    const activeElement = contentRef.current.querySelector(`[data-id="${activeId}"]`) as HTMLElement
    if (activeElement && contentRef.current) {
      const container = contentRef.current
      const elementTop = activeElement.offsetTop
      const elementHeight = activeElement.offsetHeight
      const containerHeight = container.clientHeight
      const scrollTop = container.scrollTop

      if (elementTop < scrollTop || elementTop + elementHeight > scrollTop + containerHeight) {
        container.scrollTo({
          top: elementTop - containerHeight / 2 + elementHeight / 2,
          behavior: 'smooth'
        })
      }
    }
  }, [activeId])

  const h2Count = headings.filter(h => h.level === 2).length

  return (
    <nav
      ref={tocRef}
      className="toc-premium"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ visibility: headings.length === 0 ? 'hidden' : 'visible' }}
    >
      {/* Scroll progress bar */}
      <div
        className="toc-progress-track"
        style={{
          '--progress-height': `${scrollProgress}%`
        } as React.CSSProperties}
      >
        <div
          className="toc-progress-bar"
          style={{
            height: `${scrollProgress}%`,
            opacity: scrollProgress > 0 ? 1 : 0
          }}
        >
          <div className="toc-shimmer" />
        </div>
      </div>

      {/* Header */}
      <div className="toc-header">
        <div className="flex flex-col gap-1">
          <span className="toc-title-text">{t('toc.title')}</span>
          <span className="text-[10px] text-[var(--text-muted)]">
            {h2Count} {h2Count !== 1 ? t('toc.sectionsPlural') : t('toc.sectionsSingular')}
          </span>
        </div>
        <span
          className="toc-progress-text"
          title={t('toc.scrollProgress')}
        >
          {Math.round(scrollProgress)}%
        </span>
      </div>

      {/* Content */}
      <div ref={contentRef} className="toc-content">
        {/* Active indicator line */}
        <div className="toc-indicator-track">
          <div
            className="toc-indicator"
            style={{
              transform: `translateY(${indicatorStyle.top}px)`,
              height: `${indicatorStyle.height}px`,
              opacity: indicatorStyle.opacity,
            }}
          />
        </div>

        {/* Items */}
        <ul ref={listRef} className="toc-list">
          {headings.map((heading, index) => {
            const isActive = activeId === heading.id
            const activeIndex = headings.findIndex(h => h.id === activeId)
            const isPast = activeIndex > index

            return (
              <li
                key={heading.id}
                data-id={heading.id}
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateX(0)' : 'translateX(-12px)',
                  transition: `opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${index * 40}ms, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${index * 40}ms`,
                }}
              >
                <a
                  href={`#${heading.id}`}
                  className={clsx(
                    'toc-link',
                    heading.level === 3 && 'toc-link-nested',
                    isActive && 'active',
                    isPast && !isActive && 'toc-link-past'
                  )}
                  onClick={(e) => {
                    e.preventDefault()
                    const el = document.getElementById(heading.id)
                    if (el) {
                      const yOffset = -80
                      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset
                      window.scrollTo({ top: y, behavior: 'smooth' })
                      setActiveId(heading.id)
                    }
                  }}
                >
                  {heading.level === 2 && (
                    <span className={clsx(
                      'toc-section-icon',
                      isActive && 'active'
                    )}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                        <rect x="1" y="1" width="3" height="3" rx="0.5" />
                        <rect x="6" y="1" width="3" height="3" rx="0.5" />
                        <rect x="1" y="6" width="3" height="3" rx="0.5" />
                        <rect x="6" y="6" width="3" height="3" rx="0.5" />
                      </svg>
                    </span>
                  )}
                  <span className="toc-link-text">{heading.text}</span>
                  {isActive && (
                    <span className="toc-link-dot" />
                  )}
                </a>
              </li>
            )
          })}
        </ul>
      </div>

      {/* Fade at bottom */}
      <div className="toc-fade-bottom" />

      {/* Back to top button */}
      {isHovered && scrollProgress > 10 && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="absolute bottom-6 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(252,135,183,0.15)] border border-[rgba(255,255,255,0.1)] hover:border-[rgba(252,135,183,0.3)] transition-all duration-200 z-10 group"
          title={t('toc.backToTop')}
        >
          <svg
            className="w-4 h-4 text-[var(--text-tertiary)] group-hover:text-[var(--rex-pink)] transition-colors"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </nav>
  )
}
